import type { HubGame } from '@/types';

/**
 * Portrait key art shipped with the hub (shared with the RichyPlay landing page).
 * Used when an upstream catalog has no artwork of its own.
 */
const BUNDLED_THUMBS = [
  'blackjack',
  'casineon-royal',
  'cat-plush',
  'cosmic-jackpot',
  'cute-crush',
  'farm-plush',
  'fishland-treasure',
  'flaming-phoenix-rebirth',
  'gemstone-alchemist',
  'inannas-quest',
  'midnight-cabaret',
  'mythic-forge',
  'panthers-reign',
  'pharaohs-codex',
  'private-room',
  'richy-tables',
  'roulette',
  'spin-plush',
  'temptation-garden',
  'treasure-of-the-lost-temple',
  'wukongs-treasure',
] as const;

/** Upstream titles that do not slugify onto a bundled filename. */
const TITLE_ALIASES: Record<string, string> = {
  'mytic-forge': 'mythic-forge',
  'panthers-reign-jungle-king': 'panthers-reign',
  'bevel-queen': 'gemstone-alchemist',
};

/** Game type / tag keywords mapped to key art, longest keyword wins. */
const KEYWORD_ART: Array<[string, string]> = [
  ['blackjack', 'blackjack'],
  ['roulette', 'roulette'],
  ['baccarat', 'richy-tables'],
  ['poker', 'richy-tables'],
  ['dice', 'richy-tables'],
  ['show', 'midnight-cabaret'],
  ['table', 'richy-tables'],
  ['live', 'casineon-royal'],
];

const thumbPath = (slug: string) => `/game-thumbs/${slug}.webp`;

/** Landscape 366×160 stills for the Game Information header. */
const WIDE_THUMBS: Record<string, string> = {
  'bevel-queen': '366x160_EN_BevelQueen_withRP.avif',
  bevelqueen: '366x160_EN_BevelQueen_withRP.avif',
  'gemstone-alchemist': '366x160_EN_BevelQueen_withRP.avif',
  'inannas-quest': '366x160_EN_InannaQuest_withRP.avif',
  'panthers-reign': '366x160_EN_Panther_RJK_withRP.avif',
  'panthers-reign-jungle-king': '366x160_EN_Panther_RJK_withRP.avif',
  'treasure-of-the-lost-temple': '366x160_EN_TOTLT-withRP.avif',
  'treasure-temple': '366x160_EN_TOTLT-withRP.avif',
  'cute-crush': '366x160_EN_CuteCrush-withRP.avif',
  'mythic-forge': '366x160_EN_MythicForge_withRP.avif',
  'mytic-forge': '366x160_EN_MythicForge_withRP.avif',
  'fishland-treasure': '366x160_EN_FishlandTreasure_withRPP.avif',
  'wukongs-treasure': '366x160_EN_WukongsTreasue_withRP.avif',
  'pharaohs-codex': '366x160_EN_PharaohsCodex_withRP.avif',
  'flaming-phoenix': '366x160_EN_FlamingPhoenixRebirth_withRP.avif',
  'flaming-phoenix-rebirth': '366x160_EN_FlamingPhoenixRebirth_withRP.avif',
  'cosmic-jackpot': '366x160_EN_COSMICJACKPOT_withRP.avif',
  'cat-plush': '366x160_EN_CatPlush_withRP.avif',
  'farm-plush': '366x160_EN_FarmPlush_withRP.avif',
  'spin-plush': '366x160_EN_SpinPlush_withRP.avif',
  'casineon-royal': '366x160_EN_CasineonRoyal_withRP.avif',
  'casino-neon': '366x160_EN_CasineonRoyal_withRP.avif',
  'midnight-cabaret': '366x160_EN_MidnightCabaret_withRP.avif',
  'temptation-garden': '366x160_EN_TemptationGarden_withRP.avif',
  'private-room': '366x160_EN_PrivateRoom_withRP.avif',
};

/**
 * Table families where one 366×160 still covers every variant, so "Roulette
 * Standard", "Auto Roulette" and the rest all resolve. Only consulted after the
 * per-title lookup, so a table with its own still keeps it.
 */
const WIDE_THUMB_FAMILIES: Array<[string, string]> = [
  ['blackjack', '366x160_Royal-Blackjack.avif'],
  ['roulette', '366x160_Royal-Roulette.avif'],
];

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Stable pick so a game always shows the same art across reloads. */
function hashPick(seed: string, pool: readonly string[]): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return pool[hash % pool.length];
}

function matchBySlug(slug: string): string | null {
  if (!slug) return null;
  const aliased = TITLE_ALIASES[slug] ?? slug;
  if ((BUNDLED_THUMBS as readonly string[]).includes(aliased)) return aliased;

  const candidates = [...BUNDLED_THUMBS]
    .filter((name) => aliased.startsWith(name) || name.startsWith(aliased))
    .sort((a, b) => b.length - a.length);

  return candidates[0] ?? null;
}

function matchByKeyword(game: HubGame): string | null {
  const haystack = [game.category, ...(game.tags ?? [])].join(' ').toLowerCase();
  const hit = KEYWORD_ART.find(([keyword]) => haystack.includes(keyword));
  return hit ? hit[1] : null;
}

export function isUsableUrl(url?: string | null): url is string {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

/** Hub-relative library media paths (fetched with Client Hub JWT). */
export function isHubMediaPath(url?: string | null): url is string {
  return typeof url === 'string' && url.startsWith('/library/games/') && url.includes('/media/');
}

/**
 * Poster art for a game tile: the operator-managed catalog image when present,
 * otherwise RichyPlay key art chosen from the title, then the game type.
 */
export function gamePoster(game: HubGame): string {
  if (isUsableUrl(game.thumbUrl)) return game.thumbUrl;

  const slug = matchBySlug(slugify(game.title ?? ''));
  if (slug) return thumbPath(slug);

  const keyword = matchByKeyword(game);
  if (keyword) return thumbPath(keyword);

  return thumbPath(hashPick(game.id ?? game.title ?? 'richyplay', BUNDLED_THUMBS));
}

/** Fallback art used when the upstream poster URL fails to load. */
export function gamePosterFallback(game: HubGame): string {
  const slug = matchBySlug(slugify(game.title ?? '')) ?? matchByKeyword(game);
  return thumbPath(slug ?? hashPick(game.id ?? 'richyplay', BUNDLED_THUMBS));
}

function wideThumbFile(slug: string): string | null {
  if (!slug) return null;
  return WIDE_THUMBS[slug] ?? WIDE_THUMBS[slug.replace(/-/g, '')] ?? null;
}

/** Landscape banner for the Game Information card, when a 366×160 still exists. */
export function gameWideThumb(game: HubGame): string | null {
  const titleSlug = slugify(game.title ?? '');
  const idSlug = slugify(game.id ?? '');
  const keys = [
    titleSlug,
    idSlug,
    TITLE_ALIASES[titleSlug],
    TITLE_ALIASES[idSlug],
    matchBySlug(titleSlug),
    matchBySlug(idSlug),
  ];

  for (const key of keys) {
    const file = key ? wideThumbFile(key) : null;
    if (file) return `/game-thumbs/${file}`;
  }

  const haystack = `${titleSlug} ${idSlug}`;
  const family = WIDE_THUMB_FAMILIES.find(([keyword]) => haystack.includes(keyword));
  if (family) return `/game-thumbs/${family[1]}`;

  return null;
}

/**
 * Hover preview for a library tile.
 * - Live tables: authenticated GIF via the hub media proxy.
 * - Slot games: public H.264 intro MP4 on the hub CDN (muted + playsInline).
 */
export type MotionPreview =
  | { kind: 'auth-gif'; path: string }
  | { kind: 'public-video'; url: string };

/** Same-origin public intro MP4s uploaded to the hub CDN (`/intros/<slug>.mp4`). */
export function isPublicIntroVideo(url?: string | null): url is string {
  return typeof url === 'string' && /^\/intros\/[a-z0-9-]+\.mp4$/i.test(url);
}

export function gameMotionPreview(game: HubGame): MotionPreview | null {
  const url = game.motionClipUrl;
  if (!url) return null;
  if (isHubMediaPath(url)) return { kind: 'auth-gif', path: url };
  if (isPublicIntroVideo(url)) return { kind: 'public-video', url };
  return null;
}

/**
 * Live GIF path only. Prefer `gameMotionPreview` when slot intros also matter.
 */
export function gameMotionClip(game: HubGame): string | null {
  const preview = gameMotionPreview(game);
  return preview?.kind === 'auth-gif' ? preview.path : null;
}

/**
 * Scene behind each category panel. The panel crops this hard — to a narrow
 * portrait strip when closed — so these are all wide shots with the subject
 * near the centre.
 */
const CATEGORY_SCENES: Record<string, string> = {
  live: '/banners/category-live-blackjack.webp',
  slot: '/banners/mythic-forge.webp',
  slots: '/banners/mythic-forge.webp',
  table: '/banners/table-category.webp',
  instant: '/banners/pharaohs-codex.webp',
};

const FALLBACK_SCENE = '/banners/blackjack.webp';

const CATEGORY_VIDEOS: Record<string, string> = {
  live: '/banners/live-category1.mp4',
  slot: '/banners/fishland1.mp4',
  slots: '/banners/fishland1.mp4',
};

/** Backdrop for a category panel. */
export function categoryScene(categoryId: string): string {
  return CATEGORY_SCENES[String(categoryId).toLowerCase()] ?? FALLBACK_SCENE;
}

/** Motion backdrop when a category has a video scene. */
export function categorySceneVideo(categoryId: string): string | null {
  return CATEGORY_VIDEOS[String(categoryId).toLowerCase()] ?? null;
}

